// Client-side idle timer for the picker. Cosmetic only — the
// canonical auto-pause runs server-side in app/jobs/picking-jobs.js
// because mobile browsers can't be trusted to fire setTimeout when
// the screen is off. The client-side timer just nudges the picker
// onto the selector with a friendly toast if they leave the page
// idle past the threshold while the tab is alive.
//
// API shape:
//   const idle = useIdleTimer({ enabled, timeoutMs, onIdle })
//   idle.kick()  → resets the countdown (call after every accepted scan)
import { useCallback, useEffect, useRef } from "react";

interface Args {
    enabled: boolean;
    timeoutMs: number;
    onIdle: () => void;
}

export const usePickingIdleTimer = ({ enabled, timeoutMs, onIdle }: Args) => {
    const timerRef = useRef<number | null>(null);
    const onIdleRef = useRef(onIdle);
    onIdleRef.current = onIdle;

    const clear = useCallback(() => {
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const kick = useCallback(() => {
        clear();
        if (!enabled || !Number.isFinite(timeoutMs) || timeoutMs <= 0) return;
        timerRef.current = window.setTimeout(() => onIdleRef.current(), timeoutMs);
    }, [enabled, timeoutMs, clear]);

    useEffect(() => {
        kick();
        return clear;
    }, [kick, clear]);

    return { kick, clear };
};
